import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useSelector } from 'react-redux';
import { uid } from 'react-uid';
import type {
  CommissionAttemptResult as CommissionResult,
  Job,
  JobStatusUpdate,
  ResponseGetJob,
} from '../../../../app/ApiGen';
import { useUpdateJob } from '../../../../app/ApiGen';
import { CommissionAttemptResult, JobStatus } from '../../../../app/enums';
import { JobStatusValue } from '../../../../components/CustomFields/JobStatus';
import { errorSnackbar } from '../../../../components/ErrorSnackbar';
import { Card, FormActions, List, ListHeader, ListItem, Section } from '../../../../design-system';
import { CardTitle } from '../../../../design-system/Card/styled';
import { fullStateSelector } from '../../../../store/root';
import { DateFormatDateFns, TimeFormatDateFns } from '../../../../utils/dateFormat';
import { canUpdateJobStatus } from '../../../../utils/jobs/status';
import type { Normalised } from '../../../../utils/request';

type JobProgressProps = { job: Normalised<Job> };

type JobStatusSectionProps = { job: Normalised<Job>; refetchJob: () => Promise<ResponseGetJob | null> };

const JobProgressSection: React.VFC<JobProgressProps> = ({ job }) => {
  const { progress: jobProgress } = job.details;

  if (!jobProgress) {
    return <></>;
  }

  return (
    <Section>
      <CardTitle>Job Progress</CardTitle>

      <List inset variant="bordered">
        <ListHeader>
          <Grid container>
            <Grid item xs={4}>
              Step
            </Grid>
            <Grid item xs={4}>
              Result
            </Grid>
            <Grid item xs={4}>
              Action
            </Grid>
          </Grid>
        </ListHeader>
        <ListItem>
          <Grid container>
            <Grid item xs={4}>
              Pre-start HSE (JSEA)
            </Grid>
            <Grid item xs={4}>
              {jobProgress.preWorkForm}
            </Grid>
          </Grid>
        </ListItem>

        <ListItem>
          <Grid container>
            <Grid item xs={4}>
              Commission charger
            </Grid>
            <Grid item xs={4}>
              {jobProgress.commissioning}
            </Grid>
          </Grid>
        </ListItem>

        {job.details.isAGLJob && (
          <ListItem>
            <Grid container>
              <Grid item xs={4}>
                Completion / Sign-off
              </Grid>
              <Grid item xs={4}>
                {jobProgress.postWorkForm}
              </Grid>
            </Grid>
          </ListItem>
        )}
        <ListItem>
          <Grid container>
            <Grid item xs={4}>
              Certificate of Compliance
            </Grid>
            <Grid item xs={4}>
              {jobProgress.certificateOfCompliance}
            </Grid>
          </Grid>
        </ListItem>
      </List>
      <Box m={1} />
    </Section>
  );
};

const HumanReadableCommissionResult: Record<CommissionResult, string> = {
  [CommissionAttemptResult.Success]: 'Successfully commissioned',
  [CommissionAttemptResult.AccountNotActive]: 'Failed - Customer account not active',
  [CommissionAttemptResult.ChargePointAlreadyClaimed]: 'Failed - Charge point already claimed',
  [CommissionAttemptResult.ChargePointUnresponsive]: 'Failed - Charge point unresponsive',
  [CommissionAttemptResult.FailedForUnknownReason]: 'Failed for unknown reason',
  [CommissionAttemptResult.JobHasIncorrectParameters]: 'Failed - Job has incorrect parameters',
};

export const JobStatusSection: React.VFC<JobStatusSectionProps> = ({ job, refetchJob }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { mutate } = useUpdateJob({ jobId: job.id });

  const { isEvnexAdmin } = useSelector(fullStateSelector);
  const jobStatusCanBeUpdated = canUpdateJobStatus({ isEvnexAdmin, job });

  const handleUpdateJobStatus = async (status: JobStatusUpdate) => {
    mutate({
      data: {
        id: job.id,
        type: 'jobs',
        attributes: {
          status,
        },
      },
    })
      .then(async () => {
        await refetchJob();
        enqueueSnackbar('Job status updated', { variant: 'success' });
      })
      .catch((err) => {
        errorSnackbar('Unable to edit job status', closeSnackbar, enqueueSnackbar, err);
      });
  };

  return (
    <Section>
      <Card title="Job Status">
        <List>
          <ListItem>
            <Grid container spacing={3}>
              <Grid item>
                <JobStatusValue value={job.status} />
              </Grid>
              {!jobStatusCanBeUpdated && <Grid item>The job status cannot be updated.</Grid>}
            </Grid>
          </ListItem>
        </List>
        {jobStatusCanBeUpdated && (
          <FormActions>
            <Button
              color="inherit"
              onClick={() => {
                handleUpdateJobStatus(JobStatus.Cancelled).catch(() => {});
              }}
              variant="outlined"
            >
              Cancel Job
            </Button>
          </FormActions>
        )}
        <Box m={1} />
        <List inset variant="bordered">
          <ListHeader>
            <Grid container>
              <Grid item xs={4}>
                Date
              </Grid>
              <Grid item xs={8}>
                Result
              </Grid>
            </Grid>
          </ListHeader>
          {job.commissionResults && job.commissionResults.length > 0 ? (
            job.commissionResults.map(({ date, result }) => (
              <ListItem key={uid(date)}>
                <Grid container>
                  <Grid item xs={4}>
                    {format(new Date(date), `${DateFormatDateFns.Long} ${TimeFormatDateFns.WithSeconds}`)}
                  </Grid>
                  <Grid item xs={8}>
                    {HumanReadableCommissionResult[result]}
                  </Grid>
                </Grid>
              </ListItem>
            ))
          ) : (
            <ListItem>
              <Grid container justifyContent="center">
                <Grid item>No commissioning attempts</Grid>
              </Grid>
            </ListItem>
          )}
        </List>
        <Box m={1} />
        <JobProgressSection job={job} />
      </Card>
    </Section>
  );
};
